/* eslint-disable @typescript-eslint/no-explicit-any */
import clsx from 'clsx';
import React, { useRef, useState, useEffect } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { createPortal } from 'react-dom';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';

import {
  IconX,
  IconCheck,
  IconSearch,
  IconChevronDown,
} from '@tabler/icons-react';

import { countryCodeData } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';

interface IPhoneNumberInputProps {
  name: string;
  label: string;
  register?: UseFormRegister<any>;
  setValue?: UseFormSetValue<any>;
  error?: string | string[];
  placeholder?: string;
  disabled?: boolean;
  countrySelected?: string;
  readOnly?: boolean;
  countryFlag?: boolean;
  required?: boolean;
  countryCodeName?: string;
  dataTestId?: string;
}

const PhoneNumberInput: React.FC<IPhoneNumberInputProps> = ({
  name,
  label,
  register,
  setValue,
  error,
  placeholder,
  disabled = false,
  countrySelected = '+91', // Default to India
  readOnly,
  countryFlag = false,
  required = false,
  countryCodeName = 'countryCode',
  dataTestId = 'phone-input',
}) => {
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const country = countryCodeData.find(
      (c) => c.code === countrySelected || c.phone === countrySelected
    );
    return (
      country ||
      countryCodeData.find((c) => c.phone === '+91') ||
      countryCodeData[0]
    );
  });
  const { formatText } = useFormatText();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [targetWidth, setTargetWidth] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect();

      setPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [targetRef]);

  useEffect(() => {
    if (phoneRef.current) {
      const rect = phoneRef.current.getBoundingClientRect();

      setTargetWidth(rect.width);
    }
  }, [phoneRef]);

  useEffect(() => {
    if (countrySelected) {
      const country = countryCodeData.find(
        (c) => c.code === countrySelected || c.phone === countrySelected
      );
      if (country && country.phone !== selectedCountry.phone) {
        setSelectedCountry(country);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countrySelected]);

  // Ensure form value is set on mount if not already set
  useEffect(() => {
    if (setValue) {
      // Store the phone prefix (e.g., '+91') not the country code (e.g., 'IN')
      setValue(countryCodeName, selectedCountry.phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modalStyle: React.CSSProperties = {
    position: 'absolute',
    top: error ? `${position.top + 75}px` : `${position.top + 45}px`,
    left: `${position.left}px`,
    width: `${targetWidth}px`,
  };

  const handleCountrySelect = (country: (typeof countryCodeData)[number]) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchTerm('');
    // Store the phone prefix (e.g., '+91') not the country code (e.g., 'IN')
    setValue?.(countryCodeName, country.phone);
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredCountries = normalizedSearch
    ? countryCodeData.filter(
        (option) =>
          option.name.toLowerCase().includes(normalizedSearch) ||
          option.phone.toLowerCase().includes(normalizedSearch) ||
          option.code.toLowerCase().includes(normalizedSearch)
      )
    : countryCodeData;

  useEffect(() => {
    // Close the dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="w-full flex flex-col relative gap-1.5">
      {/* Label */}
      <label
        htmlFor={name}
        className="text-sm font-medium text-neutral-primary leading-5"
      >
        {label} {required && <span className="text-error-default">*</span>}
      </label>
      <div
        ref={phoneRef}
        className={clsx(
          'group w-full rounded-lg flex overflow-hidden border border-neutral-200 shadow-s',
          {
            'bg-neutral-100! focus-within:outline-none': disabled,
            'border-error-default focus-within:outline-none':
              error && !disabled,
            'border-neutral-400 focus-within:outline-2 focus-within:outline-offset-2 hover:border-brand-default focus-within:border-[#e3ecff] focus-within:outline-[#e3ecff] text-neutral-900 font-semibold text-base':
              !disabled && !error,
            'border-neutral-200! focus-within:outline-none!': readOnly,
          }
        )}
      >
        {/* Country Code Box */}
        <div
          className={clsx(
            'bg-neutral-50 py-2.5 px-3 flex items-center justify-between cursor-pointer gap-0.5 border-r border-neutral-200 text-sm font-medium text-neutral-primary',
            disabled && 'cursor-not-allowed! bg-neutral-100!'
          )}
          onClick={() => {
            if (!disabled && !readOnly) {
              setIsOpen(!isOpen);
              setSearchTerm('');
            }
          }}
          ref={targetRef}
        >
          {countryFlag ? (
            <>
              <ReactCountryFlag
                style={{
                  fontSize: '1.5rem',
                  lineHeight: '1.5rem',
                }}
                countryCode={selectedCountry?.code}
                svg
              />
              <p>{selectedCountry?.phone}</p>
            </>
          ) : (
            <>
              <p>{selectedCountry?.phone}</p>
              <IconChevronDown
                className="text-neutral-400"
                width={16}
                height={16}
              />
            </>
          )}
        </div>

        {/* Input Field */}
        <input
          type="tel"
          readOnly={readOnly}
          pattern="[0-9]*"
          placeholder={placeholder}
          data-testid={dataTestId}
          {...register?.(name, {
            pattern: /^\d*$/,
          })}
          ref={(e) => {
            inputRef.current = e;
            register?.(name).ref(e);
          }}
          onKeyDown={(e) => {
            if (
              e.key === 'e' ||
              e.key === 'E' ||
              e.key === '+' ||
              e.key === '-' ||
              e.key === '.'
            ) {
              e.preventDefault();
            }
          }}
          onPaste={(e) => {
            const pastedData = e.clipboardData.getData('Text');
            if (
              pastedData.includes('e') ||
              pastedData.includes('E') ||
              pastedData.includes('+') ||
              pastedData.includes('-') ||
              pastedData.includes('.')
            ) {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            // Keep focus when clearing
            const currentPosition = e.target.selectionStart;
            register?.(name).onChange(e);
            // Restore focus and cursor position
            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(
                  currentPosition,
                  currentPosition
                );
              }
            }, 0);
          }}
          disabled={disabled}
          className={clsx(
            'w-full px-3 py-2.5 text-sm focus:outline-none border-l-0',
            disabled
              ? 'bg-neutral-100! cursor-not-allowed'
              : 'border-l border-neutral-400'
          )}
        />
      </div>
      {/* Custom Dropdown Menu (Hidden by Default) */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={modalStyle}
            className="p-1 flex flex-col gap-0.5 bg-white shadow-md border border-neutral-50 rounded-lg max-h-56 z-50"
          >
            {/* Search */}
            <div className="p-1.5 border-b border-neutral-100">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 focus-within:border-brand-default">
                <IconSearch size={16} className="text-neutral-400 shrink-0" />
                <input
                  ref={searchRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={formatText('common.actions.search')}
                  className="w-full text-sm text-neutral-primary placeholder:text-neutral-400 focus:outline-none"
                />
                {searchTerm && (
                  <IconX
                    size={16}
                    className="text-neutral-400 shrink-0 cursor-pointer hover:text-neutral-primary"
                    onClick={() => {
                      setSearchTerm('');
                      searchRef.current?.focus();
                    }}
                  />
                )}
              </div>
            </div>
            <div className="flex flex-col gap-0.5 overflow-y-auto">
              {filteredCountries?.length > 0 ? (
                filteredCountries?.map((option) => (
                  <div
                    key={option.id}
                    className={clsx(
                      'flex py-1.5 px-2 items-center gap-2 cursor-pointer rounded-lg  hover:bg-primary-50 text-sm font-medium text-neutral-primary ',
                      selectedCountry.id === option.id ? 'bg-primary-50' : ''
                    )}
                    onClick={() => handleCountrySelect(option)}
                  >
                    {countryFlag && (
                      <ReactCountryFlag
                        style={{
                          fontSize: '1.6rem',
                          lineHeight: '1.6rem',
                        }}
                        countryCode={option?.code}
                        svg
                      />
                    )}
                    <span>{option?.phone}</span>
                    <span className="w-full">{option?.name}</span>
                    {selectedCountry?.id === option?.id && (
                      <IconCheck
                        stroke={1.5}
                        className="text-success-primary"
                        size={20}
                      />
                    )}
                  </div>
                ))
              ) : (
                <span className="py-2 px-2 text-sm text-neutral-secondary text-center">
                  {formatText('common.no-data-found')}
                </span>
              )}
            </div>
          </div>,
          document.body
        )}
      {/* Validation Error */}
      {error && (
        <p className="text-sm text-error-default font-normal">
          {' '}
          {Array.isArray(error) ? error.join(', ') : error}{' '}
        </p>
      )}{' '}
    </div>
  );
};

export default PhoneNumberInput;
